import { useEffect, useMemo, useState } from 'react'
import { v4 } from 'uuid'
import { useStore } from '../../store'
import { CUSTOM_FIELDS_ACTIONS } from '../../store/customFields'
import { authedAxios } from '../../utils'
import useCompanyConfig from './useCompanyConfig'

const getTypeIdByKey = (key, types) => types.find((t) => t.dataType === key).id

const useCustomFields = () => {
  const {
    state: {
      customFields,
      customFieldsExp,
      customFieldTypes,
      customFieldTypesExp,
    },
    dispatch,
  } = useStore()

  const { companyConfig, patchUpdateCompanyConfig } = useCompanyConfig()

  const [localCopy, setLocalCopy] = useState([])

  const [isLoading, setIsLoading] = useState(true)
  const [hasLoadError, setHasLoadError] = useState(false)

  const commitToStore = (data) => {
    dispatch({
      type: CUSTOM_FIELDS_ACTIONS.SET_CUSTOM_FIELDS,
      customFields: data || localCopy,
    })
  }

  const saveCustomFields = () => {
    return authedAxios.put('/api/custom-fields', localCopy).then((res) => {
      getCustomFields(true)
      if (!companyConfig.hasSavedCustomFields) {
        return patchUpdateCompanyConfig({
          hasSavedCustomFields: true,
        })
      }
    })
  }

  const reset = () => setLocalCopy(customFields)

  const example = useMemo(
    () =>
      !customFieldTypes.length
        ? []
        : [
            {
              id: v4(),
              name: 'Specialty Type',
              options: [
                { id: v4(), name: 'Pediatrician' },
                { id: v4(), name: 'Dermatologist' },
                { id: v4(), name: 'Optometrist' },
              ],
              optionsOpen: true,
              typeId: getTypeIdByKey('dropdown', customFieldTypes),
            },
            {
              id: v4(),
              name: 'Service offered',
              typeId: getTypeIdByKey('string', customFieldTypes),
              options: [],
            },
            {
              id: v4(),
              name: 'Number of doctors',
              typeId: getTypeIdByKey('integer', customFieldTypes),
              options: [],
            },
          ],
    [customFieldTypes]
  )

  const getFieldTypes = () => {
    if (customFieldTypes?.length > 0 && Date.now() < customFieldTypesExp) {
      return Promise.resolve()
    }

    return authedAxios.get('/api/custom-fields/types').then((res) => {
      dispatch({
        type: CUSTOM_FIELDS_ACTIONS.SET_CUSTOM_FIELD_TYPES,
        customFieldTypes: res.data.types,
      })
    })
  }

  const getCustomFields = (force) => {
    if (!force && customFields?.length > 0 && Date.now() < customFieldsExp) {
      setLocalCopy(customFields)
      return Promise.resolve()
    }

    return authedAxios.get('/api/custom-fields').then((res) => {
      const newVal = res.data.customFields
      setLocalCopy(newVal)
      commitToStore(newVal)
    })
  }

  useEffect(() => {
    Promise.all([getCustomFields(), getFieldTypes()])
      .catch((e) => {
        setHasLoadError(true)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [])

  return {
    customFields: localCopy,
    types: customFieldTypes,
    setCustomFields: setLocalCopy,
    saveCustomFields,
    example,
    reset,
    isLoading,
    hasLoadError,
  }
}

export default useCustomFields
